<template>
  <v-popover
    ref="dueDateWrapperRef"
    interactive
    tag="div"
    content-tag="div"
    trigger="click"
    :role="'poper'"
    :on-trigger="stopPopoverTriggerPropagation"
    :on-untrigger="stopPopoverTriggerPropagation"
    :append-to="bodyRef"
    :on-show="() => !readOnly"
    placement="left-start"
    class="flex items-center text-xs"
  >
    <CardDateContent
      v-if="isDueDateSet && isLayoutList"
      :due-date-class="dueDateClass"
      :due-date-text="dateToPreview"
      :calendar-icon="getCustomIconStyle(iconPath('calendar'))"
    />
    <div
      v-if="displayAddDueDate"
      class="flex items-center"
    >
      <span
        class="flex items-center ml-2 text-text-default"
        :class="{ invisible: !cardHover }"
      >
        <em
          :style="getCustomIconStyle(iconPath('plus'))"
          class="custom-icons w-3 h-3 mr-1 bg-icon-default"
        />
        {{ translate('dueDate') }}
      </span>
    </div>
    <template #content="{ state, hide }">
      <DateTimePicker
        v-if="state.isVisible"
        :current-card-id="cardId"
        :selected-date="card.due_date"
        @dueDate="(date, rem, prop, cId) => setCardDate(date, rem, prop, cId) && hide()"
        @esc="hide"
        @close-popover="hide"
      />
    </template>
  </v-popover>
</template>

<script>
import { getHumanReadableDate } from '@/utilities/getHumanReadableDate';
import customIconsMixin from '@/mixins/customIconsMixin';
import boardListCardMixin from '@/mixins/boardListCardMixin';
import dueDateMixin from '@/mixins/dueDateMixin';
import { timestampToFullDate } from '@/utilities/timeDifference';
import CardDateContent from '@/components/functional/CardDateContent';
import { computed } from 'vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import DateTimePicker from '@/components/widgets/DateTimePicker.vue';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'CardDate',
  components: {
    CardDateContent,
    DateTimePicker,
  },
  mixins: [customIconsMixin, boardListCardMixin, dueDateMixin],
  props: {
    cardId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    cardHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    listId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
    layoutProp: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup() {
    const { embeddedBoardAttributes, embed } = useBoardEmbed();
    const { isGuestReader } = useCurrentRole();
    const readOnly = computed(() => embeddedBoardAttributes.value?.readOnly || isGuestReader.value);

    return {
      embeddedBoardAttributes,
      embed,
      readOnly,
      bodyRef: document.body,
    };
  },

  computed: {
    displayAddDueDate() {
      return !this.isDueDateSet && this.isLayoutList && !this.readOnly;
    },
    card() {
      return this.$store.getters.getCards[this.cardId] || {};
    },

    label() {
      return getHumanReadableDate(this.card.due_date * 1000);
    },
    date() {
      if (this.isDueDateSet) {
        const dueDate = new Date(this.card.due_date * 1000);
        return `${this.monthsLabel[dueDate.getMonth()]}
          ${dueDate.getDate()}`;
      }
      return '';
    },
    monthsLabel() {
      return this.translate('months').split(',');
    },

    dateToPreview() {
      return timestampToFullDate(this.card.due_date);
    },

    layout() {
      return this.embed
        ? this.embeddedBoardAttributes?.layout ||
            this.$store.getters.cardsLayout(this.embeddedBoardAttributes?.boardId)
        : this.layoutProp || this.$store.getters.cardsLayout();
    },

    isLayoutList() {
      return this.layout === 'list' || this.setLayoutListForViewsAndMyWork;
    },
    setLayoutListForViewsAndMyWork() {
      return [
        'view',
        'views',
        'newview',
        this.$constants.myWorkRouteName,
        this.$constants.memberRouteName,
      ].includes(this.$route.name);
    },
  },
  methods: {
    stopPopoverTriggerPropagation(i, event) {
      event.preventDefault();
      event.stopPropagation();
    },
    onToggleDueDatePopup() {
      if (this.readOnly) return;

      this.hideEditMode();
      this.hideCardActionsDropdown();
      this.hideMemberDropdown();

      this.$refs.dueDateWrapperRef.show();
    },
  },
};
</script>
